// utils/shareLink.ts

import copy from "copy-to-clipboard";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useCallback } from "react";

/**
 * Share Link Utility
 *
 * Provides a React hook to copy URLs to the clipboard with toast feedback.
 * Uses the copy-to-clipboard library for reliable cross-browser support.
 *
 * Features:
 * - Copy current page URL
 * - Copy any supplied URL
 * - Automatic success/error toast notifications
 * - Internationalization support
 * - Clean hook interface for React components
 *
 * @example
 * ```typescript
 * const { shareCurrentLink, shareLink } = useShareLink();
 *
 * // Share current page
 * shareCurrentLink();
 *
 * // Share specific URL
 * shareLink('https://example.com');
 * ```
 */

/**
 * React hook for share functionality with built-in translation support.
 *
 * Provides clean, parameter-free functions for sharing links. The hook handles
 * translation internally, making it easy to use in React components without
 * passing translation functions around.
 *
 * @returns Object with share functions that don't require parameters
 *
 * @example
 * ```typescript
 * import { useShareLink } from '@/utils/shareLink';
 *
 * const ChartComponent = ({ chartId }: { chartId: string }) => {
 *   const { shareCurrentLink, shareLink } = useShareLink();
 *
 *   const handleSharePage = () => {
 *     shareCurrentLink(); // Shares the current page URL
 *   };
 *
 *   const handleShareChart = () => {
 *     const chartUrl = `${window.location.origin}/charts/${chartId}`;
 *     shareLink(chartUrl); // Shares the specific chart URL
 *   };
 *
 *   return (
 *     <div>
 *       <button onClick={handleSharePage}>Share This Page</button>
 *       <button onClick={handleShareChart}>Share Chart</button>
 *     </div>
 *   );
 * };
 *
 * // Usage in menu options (like your chart components)
 * const CommitScoreGraph = () => {
 *   const { shareCurrentLink } = useShareLink();
 *
 *   return (
 *     <LineChart
 *       // ... other props
 *       menuOptions={[
 *         {
 *           label: t("studentCard.commitScoreGraph.actions.share"),
 *           onClick: shareCurrentLink, // Clean and simple!
 *           icon: <Share className="h-4 w-4" />,
 *           disabled: actionsDisabled
 *         }
 *       ]}
 *     />
 *   );
 * };
 * ```
 */
export const useShareLink = () => {
  const { t } = useTranslation();

  /**
   * Share the current page URL to clipboard.
   * Uses window.location.href to get the complete current URL including
   * query parameters, hash fragments, and protocol.
   */
  const shareCurrentLink = useCallback(() => {
    const url = window.location.href;

    try {
      const success = copy(url);

      if (success) {
        toast.success(t("global.shareLinks.linkCopied"));
      } else {
        console.error("Copy operation failed for current URL:", url);
        toast.error(t("global.shareLinks.linkError"));
      }
    } catch (error) {
      console.error("Error copying current link to clipboard:", error);
      toast.error(t("global.shareLinks.linkError"));
    }
  }, [t]);

  /**
   * Share a specific URL to clipboard.
   *
   * @param url - The URL to copy to clipboard
   */
  const shareLink = useCallback(
    (url: string) => {
      try {
        if (!url || typeof url !== "string") {
          console.error("Invalid URL provided to shareLink:", url);
          toast.error(t("global.shareLinks.linkError"));
          return;
        }

        const success = copy(url);

        if (success) {
          toast.success(t("global.shareLinks.linkCopied"));
        } else {
          console.error("Copy operation failed for URL:", url);
          toast.error(t("global.shareLinks.linkError"));
        }
      } catch (error) {
        console.error("Error copying link to clipboard:", error);
        toast.error(t("global.shareLinks.linkError"));
      }
    },
    [t],
  );

  return {
    shareCurrentLink,
    shareLink,
  };
};
